import { useGSAP } from '@gsap/react';
import gsap from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';

/**
 * One place to register the plugins, so no component has to remember to.
 *
 * `useGSAP` is registered as a plugin as well as imported as a hook — that
 * is what lets it revert every animation created inside it when the
 * component unmounts, which on a client-routed site is the difference
 * between leaving the page and leaving a half-finished tween running
 * against a detached node.
 */
gsap.registerPlugin(useGSAP, ScrollTrigger);

/**
 * The house timings. Entrances are quick and settle rather than bounce —
 * a rental listing is a page people are scanning, not a title sequence.
 */
gsap.defaults({ duration: 0.6, ease: 'power2.out' });

/**
 * Runs `build` only for readers who have not asked for less motion, and
 * reverts everything it made when the component goes away.
 *
 * Written as matchMedia rather than a plain media-query check because the
 * revert is the point: someone who turns on "reduce motion" mid-session
 * gets the animations torn down and the final state left in place, instead
 * of a page stuck at whatever opacity the tween had reached.
 */
export function withMotion(build: () => void): () => void {
  const mm = gsap.matchMedia();
  mm.add('(prefers-reduced-motion: no-preference)', build);
  return () => mm.revert();
}

/**
 * Read at the moment of the animation rather than at render, so a reader
 * who changes the setting does not have to reload to be taken seriously.
 *
 * For one-off tweens this is cheaper than `withMotion`: it collapses the
 * animation to `duration: 0` instead of standing up a matchMedia context
 * that would have to be torn down again on the next keystroke or click.
 */
export function prefersReducedMotion(): boolean {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export { gsap, ScrollTrigger, useGSAP };
